// API Endpoints for Cloud Accounts management
import { rivermeadowApi } from './api';

export const cloudAccounts = rivermeadowApi.injectEndpoints({
    endpoints: (builder) => ({
        getCloudAccounts: builder.query({
            query: ({ userOrg, size = 100, page = 0, sort = 'name,asc' }) => ({
                url: `organizations/${userOrg}/cloudaccounts?size=${size}&sort=${sort}&page=${page}`,
                method: 'GET',
            }),
            providesTags: ['CLOUD_ACCOUNTS'],
        }),
        getCloudAccountById: builder.query({
            query: (accountId) => ({
                url: `cloudaccounts/${accountId}`,
                method: 'GET',
            }),
            providesTags: (result, error, accountId) => [{ type: 'CLOUD_ACCOUNTS', id: accountId }],
        }),
        deleteCloudAccount: builder.mutation({
            query: (cloudAccountId) => ({
                url: `cloudaccounts/${cloudAccountId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['CLOUD_ACCOUNTS'],
        }),
        createCloudAccount: builder.mutation({
            query: (data) => ({
                url: 'cloudaccounts',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['CLOUD_ACCOUNTS'],
        }),

        updateCloudAccount: builder.mutation({
            query: ({ cloudAccountId, data }) => ({
                url: `cloudaccounts/${cloudAccountId}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (result, error, { cloudAccountId }) => [
                'CLOUD_ACCOUNTS',
                { type: 'CLOUD_ACCOUNTS', id: cloudAccountId },
            ],
        }),

        testCloudConnection: builder.mutation({
            query: (testPayload) => ({
                url: 'cloudaccounts/testconnection',
                method: 'POST',
                body: testPayload,
            }),
        }),

        getAzureSPNData: builder.query({
            query: (userOrg) => ({
                url: `organizations/${userOrg}/azureserviceprincipal`,
                method: 'GET',
            }),
            transformResponse: (response) => response || null,
        }),
        // Generate token for vSphere standalone
        generateApplianceToken: builder.mutation({
            query: (cloudType) => ({
                url: `cloudaccounts/appliancetokengenerator?cloud_type=${cloudType}`,
                method: 'POST',
            }),
        }),

        // Check token generation status
        getApplianceTokenStatus: builder.query({
            query: (id) => ({
                url: `cloudaccounts/appliancetokengenerator/${id}`,
                method: 'GET',
            }),
        }),

        // Generate configuration data for openshift, nc2, hpe_vme, hyperv
        generateApplianceConfiguration: builder.mutation({
            query: (cloudType) => ({
                url: `cloudaccounts/applianceconfigurationdatagenerator?cloud_type=${cloudType}`,
                method: 'POST',
            }),
        }),

        // Check configuration generation status
        getApplianceConfigurationStatus: builder.query({
            query: (id) => ({
                url: `cloudaccounts/applianceconfigurationdatagenerator/${id}`,
                method: 'GET',
            }),
        }),
        downloadCloudTemplate: builder.mutation({
            queryFn: async ({ cloudType, userOrg, templateOverride = null }, _api, _extraOptions, baseQuery) => {
                try {
                    let fileName = `rm_${cloudType === 'gcp' ? 'google' : cloudType}_cloud_template`;
                    let url = `organizations/${userOrg}/cloudtemplate?type=${cloudType}`;

                    if (templateOverride === 'shared_vpc') {
                        fileName = `rm_google_cloud_shared_vpc_template`;
                        url += '&template_override=shared_vpc';
                    }

                    fileName += cloudType === 'aws' ? '.json' : '.hcl';

                    const result = await baseQuery({
                        url,
                        method: 'GET',
                        responseHandler: (response) => response.blob(),
                    });

                    if (result.error) {
                        return { error: result.error };
                    }

                    const blob = result.data;
                    const downloadUrl = URL.createObjectURL(blob);
                    const downloadLink = document.createElement('a');
                    downloadLink.href = downloadUrl;
                    downloadLink.download = fileName;
                    document.body.appendChild(downloadLink);
                    downloadLink.click();
                    document.body.removeChild(downloadLink);
                    URL.revokeObjectURL(downloadUrl);

                    return { data: { success: true } };
                } catch (error) {
                    return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                }
            },
        }),
    }),
});

export const {
    useGetCloudAccountsQuery,
    useGetCloudAccountByIdQuery,
    useLazyGetCloudAccountsQuery,
    useLazyGetCloudAccountByIdQuery,
    useDeleteCloudAccountMutation,
    useCreateCloudAccountMutation,
    useUpdateCloudAccountMutation,
    useTestCloudConnectionMutation,
    useGetAzureSPNDataQuery,
    useLazyGetAzureSPNDataQuery,
    useDownloadCloudTemplateMutation,
    useGenerateApplianceTokenMutation,
    useLazyGetApplianceTokenStatusQuery,
    useGenerateApplianceConfigurationMutation,
    useLazyGetApplianceConfigurationStatusQuery,
} = cloudAccounts;
